import React, { useEffect, useState } from 'react';
import { Tooltip, useMediaQuery } from '@material-ui/core';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { makeStyles } from '@material-ui/core/styles';
import FiberManualRecordSharpIcon from '@material-ui/icons/FiberManualRecordSharp';
import LockIcon from '@mui/icons-material/Lock';
import RoomIcon from '@mui/icons-material/Room';

import {
  COLOR_PRIMARY,
  COLOR_PRIMARY_LIGHT,
  COLOR_AIRBNB_MARKERS,
  COLOR_RANGE_AIRBNB_MARKERS,
  exchangeRates,
  COLOR_ACCENT_1,
} from 'src/const';
import { PointerPropsType } from 'src/interfaces/iproptypes';
import mixpanel from 'mixpanel-browser';
import { IndividualListingModal } from '../../Modals';

import SimplifiedAirbnbPointerBody from '../Cards/SimplifiedAirbnbPointerBody';
import { Typography } from '@mui/material';
import {
  abbreviateNumber,
  convertCurrency,
  convertListingsDictToArray,
  getLTMData,
  isListingLegacy,
  trackUserDataVerificationAttempt,
  updateDynamoDBForUserVerification,
} from 'src/utils/functions';
import { Listing, ListingLegacy, Listings, ListingsLegacy } from 'src/interfaces/idashboard';
import { COLOR_PRIMARY_2, COLOR_SECONDARY_2_800 } from 'src/colors';

mixpanel.init('0c9d3451de8e0a1a2f21ec23b05c2063');

const useStylesRoot = (isEstimator: boolean = false) =>
  makeStyles({
    tooltip: {
      padding: 0,
      ...(isEstimator
        ? {
            minWidth: 280,
            width: '80vw',
            maxWidth: 500,
          }
        : {}),
    },
    button: {
      background: COLOR_PRIMARY,
      borderRadius: 5,
      border: 70,
      color: 'white',
      height: 50,
      width: '100%',
      padding: '10px 15px',
      boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
      '&:hover': {
        background: COLOR_PRIMARY_LIGHT,
      },
    },
    pointerRevPotentialDiv: {
      position: 'relative',
      width: '80px',
      height: '30px',
      background: '#fff',
      color: '#000',
      borderRadius: 100,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      boxShadow: '0 2px 4px #0004, 0 2px 10px #0002',
      transform: 'translate(-50%, -50%)',
      transition: 'background 0.3s ease, color 0.3s ease',
      '&:hover': {
        background: '#000',
        color: '#fff',
      },
    },
    deletedPointerRevPotentialDiv: {
      position: 'relative',
      width: '80px',
      height: '30px',
      background: '#ccc',
      backgroundImage: 'repeating-linear-gradient(-45deg, #eee, #eee 2px, #ccc 2px, #ccc 4px)',
      color: '#000',
      borderRadius: 100,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      boxShadow: '0 2px 4px #0004, 0 2px 10px #0002',
      transform: 'translate(-50%, -50%)',
    },
    lockIcon: {
      position: 'absolute',
      top: '-10px',
      right: '-5px',
      width: '20px',
      height: '20px',
      background: 'black',
      fontSize: '12px',
      borderRadius: 100,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
  });

const hoverPointer = () => {
  mixpanel.track('checkIndividualListing');
};

const Pointer = ({
  lat,
  lng,
  data,
  setListings,
  listings,
  isDisabled,
  currency,
  canDelete,
  badComps,
  setBadComps,
  setSubscriptionModalOpen,
  handleOnSampleClick,
  subscriptionModalOpen,
  multipleBookingMarkersCallLoading,
  color,
  estimatorSampleListingData,
  showRevenuePotential,
  isEstimator,
  deletedEstimatorPointer,
  setListingsDict,
  iconComponent,
  market,
  user,
  topPerformingListingOnFocus,
  hiddenListingIds,
  addHiddenListingId,
  removeHiddenListingId,
  isEstimatorLocked,
}: PointerPropsType) => {
  const matches = useMediaQuery('(max-width:550px)');
  // console.log(">>>>>>>>>>data",data)
  // console.log(">>>>>>>>>>listings",listings)

  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const [iconSize, setIconSize] = useState<number>(1);

  const [isHidden, setIsHidden] = useState<boolean>(false);

  useEffect(() => {
    // console.log('>>>>>>>>>>hiddenListingIds', hiddenListingIds);
    let hidden = false;
    if (hiddenListingIds) {
      hiddenListingIds.forEach(id => {
        if (id === data['listingID']) {
          hidden = true;
        }
      });
    }
    setIsHidden(hidden);
  }, [hiddenListingIds]);

  const isCurrentMarkerOnFocus =
    topPerformingListingOnFocus && data?.['listingID'] === topPerformingListingOnFocus;

  const getPointerSize = (adr: any = undefined) => {
    const defaultSize: number = 12;
    if (!adr || isNaN(adr)) return defaultSize;
    if (adr < 100) {
      return defaultSize;
    } else if (adr < 250) {
      return defaultSize * 1.3;
    } else {
      return defaultSize * 1.5;
    }
  };

  let averageADR = 0;
  let adrCnt = 0;

  averageADR = data['adrLTM'];

  const rootClass = useStylesRoot(isEstimator)();
  let icon: JSX.Element = isCurrentMarkerOnFocus ? (
    <RoomIcon
      style={{
        position: 'relative',
        fontSize: getPointerSize(averageADR),
        stroke: '#fff',
        // color: isCurrentMarkerOnFocus? COLOR_SECONDARY_2_800 : COLOR_PRIMARY_2,
        color: 'red',
        transformOrigin: 'center',
        transform: `scale(3) translateY(-50%)`,
        zIndex: '+9999999',
        borderRadius: 900,
        animation: 'outline-pulse 1.3s ease infinite',
      }}
      onMouseEnter={() => {
        hoverPointer();
        setIconSize(1.5);
      }}
      onMouseLeave={() => {
        setTooltipOpen(false);
        setTimeout(() => {
          setIconSize(1);
        }, 500);
      }}
      onClick={() => {
        setTooltipOpen(true);
      }}
    />
  ) : (
    <FiberManualRecordSharpIcon
      style={{
        position: 'relative',
        fontSize: getPointerSize(averageADR),
        // color: isCurrentMarkerOnFocus? COLOR_SECONDARY_2_800 : COLOR_PRIMARY_2,
        color: COLOR_PRIMARY_2,
        transformOrigin: 'center',
        transform: `scale(${iconSize})`,
        zIndex: 'inherit',
        borderRadius: 0,
        animation: 'none',
      }}
      onMouseEnter={() => {
        hoverPointer();
        setIconSize(1.5);
      }}
      onMouseLeave={() => {
        setTooltipOpen(false);
        setTimeout(() => {
          setIconSize(1);
        }, 500);
      }}
      onClick={() => {
        setTooltipOpen(true);
      }}
    />
  );

  if (showRevenuePotential) {
    const currencySymbol = exchangeRates[currency].symbol;
    //Revenue Potential
    const revenuePotential = data['revenuePotential'];
    const formattedRevenue = `${currencySymbol}${abbreviateNumber(
      convertCurrency(currency, revenuePotential)
    )}`;

    icon = (
      <div
        className={
          isHidden ? rootClass.deletedPointerRevPotentialDiv : rootClass.pointerRevPotentialDiv
        }
      >
        <Typography
          variant="body1"
          sx={{ fontWeight: 900, color: deletedEstimatorPointer ? '#aaa' : 'inherit' }}
        >
          {formattedRevenue}
        </Typography>
        {!data['is_premium'] && (
          <div className={rootClass.lockIcon}>
            <LockIcon htmlColor="#fff" fontSize="inherit" />
          </div>
        )}
      </div>
    );
  }

  if (iconComponent) {
    icon = iconComponent;
  }

  const [openFullView, setOpenFullView] = useState<boolean>(false);

  return (
    <>
      {!openFullView && (
        <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
          <Tooltip
            interactive
            // placement={matches ? "bottom" : "left"}
            classes={rootClass}
            open={tooltipOpen}
            onOpen={() => {
              setTooltipOpen(true);
              if (market) updateDynamoDBForUserVerification(market, setSubscriptionModalOpen, user);
            }}
            onClose={() => {
              !matches && setTooltipOpen(false);
            }}
            style={{
              zIndex: 999999,
            }}
            enterTouchDelay={0}
            title={
              <SimplifiedAirbnbPointerBody
                data={data as any[]}
                // setOpenFullView={deletedEstimatorPointer ? () => {} : setOpenFullView}
                setOpenFullView={setOpenFullView}
                currency={currency}
                canDelete={deletedEstimatorPointer ? true : canDelete}
                isDisabled={deletedEstimatorPointer ? true : isDisabled}
                deleteListing={() => {}}
                estimatorSampleListingData={estimatorSampleListingData}
                isEstimator={isEstimator ? true : false}
                isHidden={isHidden}
                handleOnSampleClick={handleOnSampleClick}
                hiddenListingIds={hiddenListingIds}
                addHiddenListingId={addHiddenListingId}
                removeHiddenListingId={removeHiddenListingId}
                isEstimatorLocked={isEstimatorLocked}
                isListingRecentlyAdded={data['annualRevenueLTM'] == 'N/A'}
              />
            }
          >
            {icon}
          </Tooltip>
        </ClickAwayListener>
      )}

      {openFullView && (
        <IndividualListingModal
          open={openFullView}
          onClose={() => setOpenFullView(false)}
          data={data as any[]}
          currency={currency}
          isDisabled={isEstimator ? !data['is_premium'] : isDisabled}
          listings={listings}
          setSubscriptionModalOpen={setSubscriptionModalOpen}
          handleOnSampleClick={handleOnSampleClick}
          subscriptionModalOpen={subscriptionModalOpen}
          multipleBookingMarkersCallLoading={multipleBookingMarkersCallLoading}
          estimatorSampleListingData={estimatorSampleListingData}
          isEstimator={isEstimator}
        />
      )}
    </>
  );
};

export default React.memo(Pointer);
